import Loading from '@ifca-root/react-component/src/components/Loading/Loading';
import { setAccessToken } from 'AccessToken';
import {
	useGetCurrentMealPeriodLazyQuery,
	useGetDailyMenuCategoryLazyQuery,
	useGetOutletLazyQuery,
	useGetQrLazyQuery,
	useGrantCustomerAccessMutation,
} from 'generated/graphql';
import { buildPatronReceiptPath } from 'helpers/patronReceiptNavigation';
import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

export const QRRedirect = () => {
	useEffect(() => {
		console.log('QRRedirect');
	}, []);
	const history = useHistory();
	const { outletID, qrID, accountID }: any = useParams();
	const [qrInfo, setQrInfo] = useState(null);
	const mealPeriodIdRef = useRef<string | null>(null);
	const pendingReceiptRef = useRef<{
		outletID: string;
		tableID: string;
		orderID: string;
		billID: string;
	} | null>(null);

	const rememberme: any = localStorage.getItem('rememberme');

	if (rememberme !== null) {
		localStorage.setItem('rememberme', rememberme);
	}

	const goToReceipt = (menuID: string) => {
		const target = pendingReceiptRef.current;
		if (!target?.billID || !menuID) {
			history.push(`/menu/patron-app/invalid-qr`);
			return;
		}

		history.push(
			buildPatronReceiptPath({
				outletID: target.outletID,
				tableID: target.tableID,
				orderID: target.orderID,
				menuID,
				billID: target.billID,
			}),
		);
	};

	const [
		grantCustomerAccess,
		{ loading: accessLoading },
	] = useGrantCustomerAccessMutation({
		onCompleted: ({ customerAccess }) => {
			if (customerAccess?.accessToken) {
				setAccessToken(customerAccess?.accessToken);
				sessionStorage.setItem('key', customerAccess?.accessToken);
				localStorage.setItem(
					'customerInfo',
					JSON.stringify(customerAccess.customer),
				);
				loadOutlet({
					variables: {
						ID: outletID,
					},
				});
			} else {
				throw new Error('Not authenticated');
			}
		},
	});

	const [
		loadOutlet,
		{
			loading: outletLoading,
			data: { getOutlet } = { getOutlet: [] },
		},
	] = useGetOutletLazyQuery({
		fetchPolicy: 'network-only',
		onCompleted: () => {
			loadCurrentMealPeriod({
				variables: {
					outletID: outletID,
				},
			});
		},
	});

	const [
		loadCurrentMealPeriod,
		{
			loading: getCurrentMealPeriodLoading,
			data: { getCurrentMealPeriod } = { getCurrentMealPeriod: {} as any },
		},
	] = useGetCurrentMealPeriodLazyQuery({
		fetchPolicy: 'network-only',
		onCompleted: ({ getCurrentMealPeriod }) => {
			mealPeriodIdRef.current = getCurrentMealPeriod?.ID || null;
			loadQr();
		},
	});

	const handleOrderInfoRedirect = (scannedQR: any) => {
		const orderInfo = scannedQR?.orderInfo || {};
		const hasOrderInfo = Object.keys(orderInfo).length !== 0;

		// Paid order: bill already created → receipt
		if (hasOrderInfo && orderInfo?.billID) {
			pendingReceiptRef.current = {
				outletID: scannedQR?.outletID,
				tableID: orderInfo?.tableID,
				orderID: orderInfo?.orderID,
				billID: orderInfo?.billID,
			};
			loadCategory({
				variables: {
					mealPeriodID: mealPeriodIdRef.current,
					outletID: scannedQR?.outletID,
				},
			});
			return;
		}

		// Open order, no items yet → menu list
		if (hasOrderInfo && orderInfo?.orderItemCount === 0) {
			history.push(
				`/menu/patron-app/outlet/${scannedQR?.outletID}/table/${orderInfo?.tableID}/order/${orderInfo?.orderID}`,
			);
			return;
		}

		// Open order with items → order summary
		if (hasOrderInfo && orderInfo?.orderItemCount !== 0) {
			loadCategory({
				variables: {
					mealPeriodID: mealPeriodIdRef.current,
					outletID: scannedQR?.outletID,
				},
			});
			return;
		}

		history.push(`/menu/patron-app/invalid-qr`);
	};

	const [
		loadQr,
		{ loading: qrLoading, data: { getQR } = { getQR: [] } },
	] = useGetQrLazyQuery({
		fetchPolicy: 'network-only',
		variables: { ID: qrID },
		onCompleted: ({ getQR }) => {
			if (getQR.length > 0 && getQR !== undefined && getQR !== null) {
				const scannedQR = getQR[0];
				setQrInfo(scannedQR);

				const selfOrder = getOutlet[0]?.customerPolicy?.selfOrder;
				if (selfOrder === true || selfOrder === false) {
					handleOrderInfoRedirect(scannedQR);
				}
			}
		},
	});

	const [loadCategory] = useGetDailyMenuCategoryLazyQuery({
		onError: error => {
			console.log(error);
			if (pendingReceiptRef.current?.billID) {
				// Still try receipt with a placeholder menu category segment
				goToReceipt('receipt');
			}
		},
		onCompleted: ({ getOutletDailyMenuCategory }) => {
			const menuID = getOutletDailyMenuCategory?.[0]?.ID || 'receipt';

			if (pendingReceiptRef.current?.billID) {
				goToReceipt(menuID);
				return;
			}

			history.push(
				`/menu/patron-app/outlet/${qrInfo?.outletID}/table/${qrInfo?.orderInfo?.tableID}/order/${qrInfo?.orderInfo?.orderID}/menu-category/${menuID}/summary`,
			);
		},
	});

	useEffect(() => {
		grantCustomerAccess({
			variables: {
				accountID: accountID,
				outletID: outletID,
				qrID: qrID,
			},
		});
	}, []);

	return (
		<>
			{(accessLoading ||
				outletLoading ||
				getCurrentMealPeriodLoading ||
				qrLoading) && <Loading />}
		</>
	);
};
